<template>
  <div class="trusted-by-wrapper w-full p-10 wide-bg-EXPONDO-grey-5">
    <div class="w-full flex-col md:flex-row justify-center items-center inline-flex">
      <div class="w-80 md:w-full h-36 px-2.5 py-10 flex-col">
        <div class="inline-flex md:w-full">
          <div
            class="text-center text-EXPONDO-grey text-xl md:text-3xl
            font-bold leading-7 md:leading-10 uppercase md:w-full"
          >
            {{ $t('Trusted by experts worldwide') }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="trusted-boxes w-full flex-col lg:flex-row justify-center items-center gap-9 inline-flex"
      :class="shouldWrapLayout"
    >
      <div
        class="h-48 md:h-40 px-5 py-10 bg-EXPONDO-white rounded shadow
        flex-col justify-center items-center flex float-left"
      >
        <div
          class="p-2.5 w-72 lg:w-60 xl:w-80 2xl:w-96
            justify-center items-center gap-2.5 inline-flex"
        >
          <div class="text-center text-EXPONDO-grey text-base font-normal leading-relaxed">
            {{ $t('reliable expertise') }}
          </div>
        </div>
        <div class="w-56 p-2.5 justify-center items-center gap-2.5 inline-flex">
          <div class="text-center text-EXPONDO-grey text-xl font-bold leading-7 uppercase md:text-3xl">
            {{ $t('since 2007') }}
          </div>
        </div>
      </div>
      <a
        v-if="!showTrustPilotWidget"
        href="https://www.trustpilot.com/review/expondo.com"
        target="_blank"
      >
        <div
          class="h-48 md:h-40 px-5 py-10 bg-EXPONDO-white rounded shadow flex-col justify-center items-center"
        >

          <div class="w-72 lg:w-60 xl:w-80 2xl:w-96">
            <div class="flex inline-flex justify-center items-center pb-5 w-full">
              <div class="pr-2 flex-col">
                <div class="text-EXPONDO-grey text-xl md:text-3xl font-bold leading-7 ">
                  4.3
                </div>
              </div>
              <div class="flex-col text-EXPONDO-gold">
                <div class="w-3.5 h-3.5 left-0 top-0 inline-flex">
                  <span class="iconify svg--star" />
                </div>
                <div class="w-3.5 h-3.5 left-8 top-0 inline-flex">
                  <span class="iconify svg--star" />
                </div>
                <div class="w-3.5 h-3.5 left-16 top-0 inline-flex">
                  <span class="iconify svg--star" />
                </div>
                <div class="w-3.5 h-3.5 left-32 top-0 inline-flex">
                  <span class="iconify svg--star" />
                </div>
                <div class="w-3.5 h-3.5 left-48 top-0 inline-flex">
                  <span class="iconify svg--star-half" />
                </div>
              </div>
            </div>
            <div class="justify-center items-center inline-flex w-full">
              <div class="text-center text-EXPONDO-grey text-base font-normal leading-relaxed">
                {{ $t('shop rating on TrustPilot') }}
              </div>
            </div>
          </div>
        </div>
      </a>
      <div
        v-else
      >
        <div class="h-48 md:h-40 bg-EXPONDO-white rounded shadow px-5 pt-9 pb-1 md:pt-5 md:pb-5">
          <div
            class="w-72 lg:w-60 xl:w-80 2xl:w-96"
          >
            <ClientOnly>
              <Trustbox
                :template-id="TRUSTPILOT_MINI_WIDGET_ID"
                :locale="languageAndCountry"
                :style-width="TRUSTPILOT_MINI_WIDGET_WIDTH"
                :style-height="TRUSTPILOT_MINI_WIDGET_HEIGHT"
                class="flex justify-center"
              />
            </ClientOnly>
          </div>
        </div>
      </div>
      <div
        class="h-48 md:h-40 px-5 py-10 bg-EXPONDO-white rounded shadow
        flex-col justify-center items-center flex float-left"
      >
        <div class="p-2.5 justify-center items-center gap-2.5 inline-flex">
          <div class="text-center text-EXPONDO-grey text-base font-normal leading-relaxed">
            {{ $t('serving millions of customers') }}
          </div>
        </div>
        <div
          class="w-72 lg:w-60 xl:w-80 2xl:w-96 p-2.5
          justify-center items-center gap-2.5 inline-flex"
        >
          <div
            :class="['text-center text-EXPONDO-grey font-bold leading-7 uppercase',
                     isLongLocale ? 'text-sm lg:text-xl' : 'text-xl lg:text-3xl']"
          >
            {{ $t('accros europe') }}
          </div>
        </div>
      </div>
      <div
        v-if="showHeurekaWidget"
        class="h-48 md:h-40 px-5 py-10 bg-EXPONDO-white rounded shadow
        flex-col justify-center items-center flex float-left"
      >
        <HeurekaWidget :options="heurekaWidgetOptions" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">

import {
  TRUSTPILOT_MINI_WIDGET_WIDTH,
  TRUSTPILOT_MINI_WIDGET_HEIGHT,
} from '~~/constants/ui';
import { HEUREKA_WIDGET_DATA, TRUSTPILOT_MINI_WIDGET_ID } from '~~/constants/trustPilot';

export default defineComponent({
  name: 'TrustedBy',

  setup() {

    const { languageAndCountry, countryCode } = useLocalization();
    const { showTrustPilotWidget } = useTrustPilotWidget();
    const heurekaWidgetOptions = computed(() => HEUREKA_WIDGET_DATA[countryCode.value]);
    const showHeurekaWidget = computed(() => !!heurekaWidgetOptions.value);
    const isLongLocale = computed(() => ['pl', 'de'].includes(countryCode.value));
    const shouldWrapLayout = computed(() => {

      return showHeurekaWidget.value ? 'lg:flex-wrap' : '';

    });

    return {
      isLongLocale,
      languageAndCountry,
      showTrustPilotWidget,
      TRUSTPILOT_MINI_WIDGET_ID,
      heurekaWidgetOptions,
      showHeurekaWidget,
      shouldWrapLayout,
      TRUSTPILOT_MINI_WIDGET_WIDTH,
      TRUSTPILOT_MINI_WIDGET_HEIGHT,
    };

  },
});

</script>
<style scoped>
.trusted-by-wrapper {
  color: var(--c-text);
}
</style>
